import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, type PlatformPermission, type PlatformRole } from "@/api";
import { platformRoleKeys, usePlatformRoleCatalog, usePlatformRoles } from "@/api/hooks/use-platform-roles";
import { useUsers } from "@/api/hooks/use-users";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
type Draft = {
  key: string;
  name: string;
  description: string;
  permissions: string[];
  showLockedNav: boolean;
};
const emptyDraft = (): Draft => ({
  key: "",
  name: "",
  description: "",
  permissions: ["console.access"],
  showLockedNav: false
});
const toDraft = (role: PlatformRole): Draft => ({
  key: role.key,
  name: role.name,
  description: role.description ?? "",
  permissions: role.permissions,
  showLockedNav: role.showLockedNav === true
});
export function PlatformRolesPage() {
  const {
    t
  } = useTranslation();
  const user = useAuth(s => s.user);
  const canManage = hasAdminPermission(user, "action.platform_roles.manage");
  const queryClient = useQueryClient();
  const rolesQuery = usePlatformRoles();
  const catalogQuery = usePlatformRoleCatalog();
  const roles = rolesQuery.data ?? [];
  const catalog = catalogQuery.data ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft());
  const [mode, setMode] = useState<"edit" | "create">("edit");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const selected = roles.find(role => role.key === selectedKey) ?? roles[0] ?? null;
  const loadError = rolesQuery.error ?? catalogQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);
  const loading = (rolesQuery.isPending || catalogQuery.isPending) && !loadError;
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !rolesQuery.data) return;
    seeded.current = true;
    const first = rolesQuery.data[0] ?? null;
    setSelectedKey(first?.key ?? null);
    setDraft(first ? toDraft(first) : emptyDraft());
  }, [rolesQuery.data]);
  const grouped = useMemo(() => {
    const map = new Map<string, PlatformPermission[]>();
    for (const permission of catalog) {
      map.set(permission.category, [...(map.get(permission.category) ?? []), permission]);
    }
    return [...map.entries()];
  }, [catalog]);
  const selectRole = (role_0: PlatformRole) => {
    setMode("edit");
    setSelectedKey(role_0.key);
    setDraft(toDraft(role_0));
    setActionError(null);
  };
  const toggle = (permission_0: string) => {
    setDraft(current => ({
      ...current,
      permissions: current.permissions.includes(permission_0) ? current.permissions.filter(item => item !== permission_0) : [...current.permissions, permission_0]
    }));
  };
  const save = async () => {
    setSaving(true);
    setActionError(null);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        permissions: draft.permissions,
        showLockedNav: draft.showLockedNav
      };
      const saved = mode === "create" ? await api.platformRoles.create({
        key: draft.key.trim(),
        ...payload
      }) : await api.platformRoles.update(draft.key, payload);
      queryClient.setQueryData<PlatformRole[]>(platformRoleKeys.all, current_0 => mode === "create" ? [...(current_0 ?? []), saved] : (current_0 ?? []).map(role_1 => role_1.key === saved.key ? saved : role_1));
      setSelectedKey(saved.key);
      setDraft(toDraft(saved));
      setMode("edit");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = async () => {
    setConfirmDelete(false);
    if (!selected || selected.system) return;
    setSaving(true);
    setActionError(null);
    try {
      await api.platformRoles.remove(selected.key);
      const next = roles.filter(role_2 => role_2.key !== selected.key);
      queryClient.setQueryData<PlatformRole[]>(platformRoleKeys.all, next);
      setSelectedKey(next[0]?.key ?? null);
      setDraft(next[0] ? toDraft(next[0]) : emptyDraft());
    } catch (e_0) {
      setActionError(e_0 instanceof Error ? e_0.message : String(e_0));
    } finally {
      setSaving(false);
    }
  };
  if (loading) return <p className="text-muted-foreground">{t("platformRoles.loading")}</p>;
  return <div className="flex flex-col gap-6">
		<div className="flex flex-col gap-1" data-tour="roles-intro">
			<h1 className="text-2xl font-bold">{t("nav.platformRoles")}</h1>
			<p className="text-sm text-muted-foreground">{t("platformRoles.intro")}</p>
		</div>
		<div className="grid gap-6 lg:grid-cols-[320px_1fr]">
			<Card data-tour="roles-list">
				<CardHeader className="flex flex-row items-center justify-between gap-3">
					<CardTitle>{t("platformRoles.title")}</CardTitle>
					{canManage && <Button size="sm" onClick={() => {
            setMode("create");
            setSelectedKey(null);
            setDraft(emptyDraft());
          }}>{t("platformRoles.create")}</Button>}
				</CardHeader>
				<CardContent className="grid gap-2">
					{roles.map(role_3 => <button key={role_3.key} type="button" onClick={() => selectRole(role_3)} className={cn("rounded-md border p-3 text-left", selectedKey === role_3.key && mode === "edit" ? "border-primary bg-primary/5" : "hover:bg-muted")}>
							<div className="flex items-center justify-between gap-2">
								<p className="font-medium">{role_3.name}</p>
								{role_3.system && <Badge variant="secondary">{t("platformRoles.system")}</Badge>}
							</div>
							<p className="text-xs text-muted-foreground">{role_3.key}</p>
							{role_3.userCount !== undefined && <p className="mt-1 text-xs text-muted-foreground">{t("platformRoles.users", {
                count: role_3.userCount
              })}</p>}
						</button>)}
				</CardContent>
			</Card>

			<Card data-tour="roles-permissions">
				<CardHeader>
					<CardTitle>{mode === "create" ? t("platformRoles.newTitle") : draft.name}</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-5">
					{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
					<div className="grid gap-4 sm:grid-cols-2">
						<Field label={t("platformRoles.key")}>
							<Input value={draft.key} disabled={mode === "edit"} onChange={event => setDraft(current_1 => ({
                ...current_1,
                key: event.target.value
              }))} />
						</Field>
						<Field label={t("platformRoles.name")}>
							<Input value={draft.name} disabled={!canManage} onChange={event_0 => setDraft(current_2 => ({
                ...current_2,
                name: event_0.target.value
              }))} />
						</Field>
					</div>
					<Field label={t("platformRoles.description")}>
						<textarea value={draft.description} disabled={!canManage} rows={3} onChange={event_1 => setDraft(current_3 => ({
              ...current_3,
              description: event_1.target.value
            }))} className="rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs disabled:cursor-not-allowed disabled:opacity-50" />
					</Field>

					<label className="flex items-start gap-2 rounded-md border p-3 hover:bg-muted">
						<input type="checkbox" disabled={!canManage} checked={draft.showLockedNav} onChange={() => setDraft(current_4 => ({
              ...current_4,
              showLockedNav: !current_4.showLockedNav
            }))} />
						<span>
							<span className="block text-sm font-medium">{t("platformRoles.showLockedNav")}</span>
							<span className="block text-xs text-muted-foreground">{t("platformRoles.showLockedNavHint")}</span>
						</span>
					</label>

					<div className="grid gap-4">
						{grouped.map(([category, permissions]) => <div key={category} className="rounded-md border p-4">
								{/* Категории приходят с бэкенда техническими ключами
            (system / pages / actions) — подписываем по-русски. */}
								<h3 className="mb-3 font-medium">
									{t(`platformRoles.category.${category}`, {
                  defaultValue: category
                })}
								</h3>
								<div className="grid gap-2 sm:grid-cols-2">
									{permissions.map(permission_1 => <label key={permission_1.key} className="flex items-start gap-2 rounded-md p-2 hover:bg-muted">
											<input type="checkbox" disabled={!canManage} checked={draft.permissions.includes(permission_1.key)} onChange={() => toggle(permission_1.key)} />
											<span>
												<span className="block text-sm font-medium">{permission_1.label}</span>
												{permission_1.description && <span className="block text-xs text-muted-foreground">
														{permission_1.description}
													</span>}
												<span className="block text-[11px] text-muted-foreground/70">
													{permission_1.key}
												</span>
											</span>
										</label>)}
								</div>
							</div>)}
					</div>

					{canManage && <div className="flex gap-2">
							<Button onClick={save} disabled={saving || !draft.key.trim() || !draft.name.trim()}>{saving ? t("platformRoles.saving") : t("platformRoles.save")}</Button>
							{mode === "edit" && selected && !selected.system && <Button variant="destructive" onClick={() => setConfirmDelete(true)} disabled={saving || (selected.userCount ?? 0) > 0} title={(selected.userCount ?? 0) > 0 ? t("platformRoles.deleteBlocked") : undefined}>
									{t("platformRoles.delete")}
								</Button>}
						</div>}
					{canManage && mode === "edit" && selected && !selected.system && (selected.userCount ?? 0) > 0 && <p className="text-xs text-muted-foreground">{t("platformRoles.deleteBlocked")}</p>}
				</CardContent>
			</Card>
		</div>
		{mode === "edit" && selected && <RoleMembers role={selected} />}
		<ConfirmDialog open={confirmDelete} title={t("platformRoles.deleteConfirm")} confirmLabel={t("platformRoles.delete")} confirmVariant="destructive" onConfirm={remove} onClose={() => setConfirmDelete(false)} busy={saving} />
		</div>;
}

/**
 * Сотрудники с выбранной ролью и назначение роли. Раньше на экране было
 * только «Пользователей: N», а кому роль выдана и как выдать её ещё одному
 * сотруднику — надо было искать по карточкам пользователей.
 */
function RoleMembers({
  role
}: {
  role: PlatformRole;
}) {
  const {
    t
  } = useTranslation();
  const user = useAuth(s => s.user);
  const canSeeUsers = hasAdminPermission(user, "page.users");
  const canAssign = hasAdminPermission(user, "action.users.security");
  const queryClient = useQueryClient();
  const users = useUsers();
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unassign, setUnassign] = useState<{
    id: string;
    name: string;
  } | null>(null);
  if (!canSeeUsers) {
    return <Card>
				<CardHeader>
					<CardTitle>{t("platformRoles.members.title")}</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-sm text-muted-foreground">{t("platformRoles.members.noAccess")}</p>
				</CardContent>
			</Card>;
  }
  const all = users.data ?? [];
  const members = all.filter(u => u.platformRole?.key === role.key);
  const q = query.trim().toLowerCase();
  const candidates = q.length >= 2 ? all.filter(u_0 => u_0.role !== "admin" && u_0.platformRole?.key !== role.key).filter(u_1 => `${u_1.name} ${u_1.email}`.toLowerCase().includes(q)).slice(0, 8) : [];
  const assign = async (id: string, key: string | null) => {
    setBusy(id);
    setError(null);
    try {
      await api.users.update(id, {
        platformRoleKey: key
      });
      await Promise.all([queryClient.invalidateQueries({
        queryKey: ["users"]
      }), queryClient.invalidateQueries({
        queryKey: platformRoleKeys.all
      })]);
      setQuery("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      setUnassign(null);
    }
  };
  return <Card data-testid="role-members" data-tour="roles-members">
			<CardHeader>
				<CardTitle>{t("platformRoles.members.title")}</CardTitle>
			</CardHeader>
			<CardContent className="grid gap-4">
				{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
				{users.isPending ? <p className="text-sm text-muted-foreground">{t("platformRoles.loading")}</p> : members.length === 0 ? <p className="text-sm text-muted-foreground">{t("platformRoles.members.empty")}</p> : <ul className="divide-y rounded-md border">
						{members.map(m => <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
								<Link to={`/users/${m.id}`} className="min-w-0 hover:underline">
									<span className="block truncate text-sm font-medium">{m.name}</span>
									<span className="block truncate text-xs text-muted-foreground">{m.email}</span>
								</Link>
								{canAssign && <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => setUnassign({
            id: m.id,
            name: m.name
          })}>
										{t("platformRoles.members.remove")}
									</Button>}
							</li>)}
					</ul>}
				{canAssign && <div className="grid gap-2">
						<Label htmlFor="role-member-search">{t("platformRoles.members.assign")}</Label>
						<Input id="role-member-search" value={query} placeholder={t("platformRoles.members.searchPlaceholder")} onChange={e_0 => setQuery(e_0.target.value)} />
						{q.length >= 2 && candidates.length === 0 && <p className="text-xs text-muted-foreground">{t("platformRoles.members.notFound")}</p>}
						{candidates.length > 0 && <ul className="divide-y rounded-md border">
								{candidates.map(c => <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-2">
										<span className="min-w-0">
											<span className="block truncate text-sm">{c.name}</span>
											<span className="block truncate text-xs text-muted-foreground">
												{c.email}
												{c.platformRole ? ` · ${t("platformRoles.members.currentRole", {
                  name: c.platformRole.name
                })}` : ""}
											</span>
										</span>
										<Button size="sm" disabled={busy === c.id} onClick={() => assign(c.id, role.key)}>
											{t("platformRoles.members.assignButton")}
										</Button>
									</li>)}
							</ul>}
					</div>}
				<ConfirmDialog open={Boolean(unassign)} title={t("platformRoles.members.removeConfirm", {
        name: unassign?.name ?? ""
      })} description={t("platformRoles.members.removeHint")} confirmLabel={t("platformRoles.members.remove")} confirmVariant="destructive" onConfirm={() => unassign && assign(unassign.id, null)} onClose={() => setUnassign(null)} busy={Boolean(busy)} />
			</CardContent>
		</Card>;
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Label>{label}</Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="grid gap-1.5">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}